import React, { useCallback, useEffect, useMemo, useState } from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { ValueEngagementReport } from "../components/ValueEngagementReport";
import { usePortalData } from "../hooks/usePortalData";
import { isActivatedClientStatus } from "../utils/clientStatus";

export function ValueEngagementPage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const [patientId, setPatientId] = useState("");
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getMyClients(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const patients = useMemo(
    () =>
      (data?.patients ?? [])
        .filter((patient) => isActivatedClientStatus(patient.status))
        .sort((left, right) =>
          left.accessCode.localeCompare(right.accessCode),
        ),
    [data?.patients],
  );
  const available = Boolean(data?.found && data.therapist);

  useEffect(() => {
    if (
      patientId &&
      !patients.some((patient) => patient.id === patientId)
    ) {
      setPatientId("");
    }
  }, [patientId, patients]);

  return (
    <ClinicianPortalLayout
      activeGlobalKey="value-engagement"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Value & Engagement"
        clinicianName={data?.therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[{ label: "Reports" }, { label: "Value & Engagement" }]}
          />
        }
      />

      <div className="cp-page-content cp-page-content--wide cp-page-content--profile">
        {state.status === "loading" ? (
          <LoadingState label="Loading clients" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Value engagement report is not available")}
            onRetry={state.refresh}
          />
        ) : null}
        {available && !patients.length ? (
          <EmptyState
            title="No clients available"
            description="Add a client before running the value engagement report."
          />
        ) : null}
        {available && patients.length ? (
          <ValueEngagementReport
            key={patientId || "no-client"}
            clinicianCode={clinicianCode}
            patientId={patientId}
            patients={patients}
            onPatientChange={setPatientId}
          />
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
